import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { MessageCircle, X, Send, Check } from "lucide-react";
import { t } from "@/i18n";

export default function FeedbackWidget({ open: openProp, onOpenChange }) {
  const { user } = useAuth();
  const loc = useLocation();

  const controlled = typeof openProp === "boolean";
  const [openLocal, setOpenLocal] = useState(false);
  const open = controlled ? openProp : openLocal;
  const setOpen = (v) => {
    const next = typeof v === "function" ? v(open) : v;
    if (controlled) onOpenChange?.(next);
    else setOpenLocal(next);
  };
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setOpen(false);
    setTimeout(() => {
      setMessage("");
      setEmail("");
      setSent(false);
      setError("");
    }, 200);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    if (!user && !email.trim()) {
      setError(t("feedback.widget_need_email"));
      return;
    }
    setSending(true);
    setError("");
    try {
      await api.post("/support/message", {
        message: message.trim(),
        email: email.trim() || undefined,
        page: loc.pathname,
      });
      setSent(true);
      setTimeout(reset, 2200);
    } catch {
      setError(t("feedback.widget_send_failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {open && (
        <div
          className={`fixed z-50 w-[calc(100vw-2rem)] max-w-sm rounded-2xl border bg-card shadow-2xl overflow-hidden right-4 md:right-6 ${
            controlled ? "top-20" : "bottom-24 md:bottom-24"
          }`}
        >
          <div className="px-4 py-3 border-b">
            <div className="flex items-center justify-between gap-3 mb-1">
              <span className="text-sm font-semibold">
                {t("feedback.widget_title")}
              </span>
              <button
                onClick={reset}
                className="p-1 rounded-lg hover:bg-secondary transition shrink-0"
                aria-label={t("feedback.widget_close")}
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t("feedback.widget_subtitle")}
            </p>
          </div>
          {sent ? (
            <div className="px-4 py-8 text-center">
              <Check size={28} className="mx-auto mb-2 text-green-500" />
              <p className="text-sm font-medium">
                {t("feedback.widget_sent_title")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("feedback.widget_sent_body")}
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="p-4 space-y-3">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("feedback.widget_placeholder")}
                rows={4}
                maxLength={4000}
                autoFocus
                className="w-full px-3 py-2.5 rounded-xl border bg-background text-sm outline-none focus:ring-2 ring-primary resize-none"
              />
              {!user && (
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("feedback.widget_email_placeholder")}
                  className="w-full px-3 py-2.5 rounded-xl border bg-background text-sm outline-none focus:ring-2 ring-primary"
                />
              )}
              {error && <p className="text-xs text-destructive">{error}</p>}
              <button
                type="submit"
                disabled={sending || !message.trim()}
                className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition disabled:opacity-50"
              >
                <Send size={15} />
                {sending
                  ? t("feedback.widget_sending")
                  : t("feedback.widget_send")}
              </button>
            </form>
          )}
        </div>
      )}
      {!controlled && (
        <button
          onClick={() => setOpen((o) => !o)}
          className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-40 w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:opacity-90 transition"
          aria-label={t("feedback.widget_open_label")}
          title={t("feedback.widget_open_title")}
        >
          {open ? <X size={20} /> : <MessageCircle size={20} />}
        </button>
      )}
    </>
  );
}
